import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, ArrowRight, Shield, Zap, Clock, FileText,
  Star, Users, TrendingUp, Award, Play, ChevronRight
} from 'lucide-react';
import { analytics } from '../../lib/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images générées
const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ca17ff8912835584b082f13bc0ab2607f565460cc002c370e4accc7c67ffa2e7.png',
  facturx: 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/083ccfb3dfd8e33da526db0811be1ce5b28e79bc2d9857f7caebcf0beaa172a8.png',
  calendrier: 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/e0f9098ede9c83c5a53b25d26af4ac61500b25408e856bcd4e52ecb7f9c7c29d.png'
};

const LandingFacturation2026 = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    // Track landing page view with UTM params
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');
    
    analytics.trackEvent('landing_view', {
      page: 'facturation-2026',
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign
    });

    // Track in internal analytics
    fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'landing_view',
        details: { 
          page: 'facturation-2026',
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          referrer: document.referrer
        }
      })
    }).catch(() => {});
  }, [searchParams]);

  const handleCTAClick = (buttonName) => {
    analytics.trackButtonClick(buttonName, 'landing_facturation_2026');
  };

  return (
    <>
      <Helmet>
        <title>Logiciel Facturation Électronique 2026 | Conforme Factur-X | SmartFacture AI</title>
        <meta name="description" content="Préparez votre entreprise à la facturation électronique 2026. Logiciel conforme Factur-X, PPF/PDP. Essai gratuit 7 jours. À partir de 14€/mois." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://smartfacture-ai.com/landing/facturation-2026" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Minimal Header */}
        <header className="bg-white border-b border-slate-100 py-4 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link 
              to="/register"
              onClick={() => handleCTAClick('header_cta')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Essai gratuit
            </Link>
          </div>
        </header>

        {/* Hero Section - Above the fold */}
        <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {/* Trust Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-sm mb-6">
                  <Shield className="h-4 w-4" />
                  Conforme réforme 2026
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Facturation électronique 2026 :
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400"> soyez prêt en 5 minutes</span>
                </h1>
                
                <p className="text-xl text-slate-300 mb-8">
                  Créez des factures <strong>Factur-X conformes</strong>, transmettez via PPF/PDP, et évitez les amendes de 15€/facture. Le tout automatiquement.
                </p>

                {/* Key Benefits */}
                <div className="space-y-3 mb-8">
                  {[
                    'Format Factur-X généré automatiquement',
                    'Connexion PPF/PDP intégrée',
                    'Scanner IA pour vos factures fournisseurs',
                    'Relances automatiques des impayés'
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to="/register"
                    onClick={() => handleCTAClick('hero_primary_cta')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-lg font-semibold rounded-xl shadow-xl shadow-blue-500/25 transition-all"
                  >
                    Essai gratuit 7 jours
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link 
                    to="/demo"
                    onClick={() => handleCTAClick('hero_demo_cta')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 text-lg font-medium rounded-xl transition-all"
                  >
                    <Play className="h-5 w-5" />
                    Voir la démo
                  </Link>
                </div>

                {/* Trust Indicators */}
                <div className="flex items-center gap-6 mt-8 text-sm text-slate-400">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Sans carte bancaire
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Annulation libre
                  </span>
                </div>
              </div>

              {/* Hero Image */}
              <div className="relative hidden lg:block">
                <img 
                  src={IMAGES.hero}
                  alt="Facturation électronique 2026"
                  className="rounded-2xl shadow-2xl"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Urgency Bar */}
        <section className="bg-amber-500 text-amber-900 py-3 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 text-center font-medium">
            <Clock className="h-5 w-5" />
            <span>
              <strong>Attention :</strong> Réception obligatoire dès septembre 2026. Ne risquez pas 15€ d'amende par facture.
            </span>
          </div>
        </section>

        {/* Problem/Solution Section */}
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                La réforme 2026 en 30 secondes
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Ce qui change et comment SmartFacture AI vous simplifie la vie
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Before */}
              <div className="bg-white rounded-2xl p-8 border border-red-200">
                <div className="flex items-center gap-2 text-red-600 font-semibold mb-6">
                  <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-sm">✗</span>
                  Avant (non conforme en 2026)
                </div>
                <ul className="space-y-4">
                  {[
                    'Facture PDF envoyée par email',
                    'Saisie manuelle dans Excel',
                    'Archivage papier ou local',
                    'Relances manuelles des impayés',
                    'Pas de traçabilité'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <span className="text-red-500 mt-1">✗</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* After */}
              <div className="bg-white rounded-2xl p-8 border-2 border-green-500 shadow-xl shadow-green-500/10">
                <div className="flex items-center gap-2 text-green-600 font-semibold mb-6">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-sm">✓</span>
                  Avec SmartFacture AI
                </div>
                <ul className="space-y-4">
                  {[
                    'Factur-X généré automatiquement',
                    'Scanner IA : photo → données',
                    'Export pour archivage légal',
                    'Relances automatiques programmées',
                    'Transmission PPF/PDP intégrée'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features with Image */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src={IMAGES.facturx}
                  alt="Format Factur-X expliqué"
                  className="rounded-2xl shadow-xl"
                  loading="lazy"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Factur-X : le format qui vous simplifie la vie
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  Factur-X combine un PDF lisible et des données XML structurées. Vos clients voient une facture normale, mais elle est 100% conforme à la réforme.
                </p>
                
                <div className="space-y-4">
                  {[
                    { title: 'PDF lisible', desc: 'Vos clients peuvent ouvrir et lire la facture normalement' },
                    { title: 'XML intégré', desc: 'Les données sont extraites automatiquement par les logiciels' },
                    { title: 'Archivage légal', desc: 'Format PDF/A-3 garanti pour 10 ans minimum' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Zap className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Teaser */}
        <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              À partir de 14€/mois seulement
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Moins cher qu'un café par jour pour être 100% conforme
            </p>

            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              {[
                { name: 'Starter', price: '14€', features: '30 factures/mois' },
                { name: 'Pro', price: '29€', features: 'Illimité + Scanner IA', popular: true },
                { name: 'Expert', price: '49€', features: 'Multi-utilisateurs' }
              ].map((plan, i) => (
                <div 
                  key={i} 
                  className={`p-6 rounded-2xl ${plan.popular ? 'bg-white text-slate-900 shadow-xl' : 'bg-white/10'}`}
                >
                  {plan.popular && (
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mb-3">
                      Populaire
                    </span>
                  )}
                  <h3 className={`text-lg font-semibold ${plan.popular ? 'text-slate-900' : ''}`}>{plan.name}</h3>
                  <p className={`text-3xl font-bold my-2 ${plan.popular ? 'text-blue-600' : ''}`}>{plan.price}<span className="text-sm font-normal">/mois</span></p>
                  <p className={`text-sm ${plan.popular ? 'text-slate-600' : 'text-blue-200'}`}>{plan.features}</p>
                </div>
              ))}
            </div>

            <Link 
              to="/register"
              onClick={() => handleCTAClick('pricing_cta')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 hover:bg-blue-50 text-lg font-semibold rounded-xl shadow-xl transition-all"
            >
              Commencer l'essai gratuit
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Prêt pour la facturation électronique 2026 ?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Anticipez la réforme dès maintenant
            </p>
            
            <Link 
              to="/register"
              onClick={() => handleCTAClick('final_cta')}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-xl font-semibold rounded-xl shadow-xl shadow-blue-500/25 transition-all"
            >
              Démarrer mon essai gratuit
              <ArrowRight className="h-6 w-6" />
            </Link>

            <div className="flex items-center justify-center gap-8 mt-8 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                7 jours gratuits
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                Sans carte bancaire
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                Support français
              </span>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-950 text-slate-400 py-8 px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p>© 2026 SmartFacture AI. Tous droits réservés.</p>
            <div className="flex gap-6 text-sm">
              <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white">CGU</Link>
              <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingFacturation2026;
